import {HeadFC, PageProps} from "gatsby"
import {Fragment, useContext, useState} from "react"
import Blank from "~/components/Sections/Blank/Blank"
import HeroSection from "~/components/Sections/HeroSection/HeroSection"
import Search from "~/components/Sections/Search/Search"
import Post from "~/components/Sections/Shelf/Post"
import Shelf, {ShelfSortCategories} from "~/components/Sections/Shelf/Shelf"
import Button from "~/components/UI/Button/Button"
import Gap from "~/components/UI/Gap/Gap"
import GenericLink from "~/components/UI/GenericLink/GenericLink"
import InputText from "~/components/UI/InputText/InputText"
import Reader from "~/components/UI/Reader/Reader"
import SectionContainer from "~/components/UI/SectionContainer/SectionContainer"
import Switch from "~/components/UI/Switch/Switch"
import I18nContext from "~/contexts/I18nContext"
import TransitionContext from "~/contexts/TransitionContext"
import {useAcademyPostsQuery} from "~/queries/AcademyPostsQuery"
import {useBlogPostsQuery} from "~/queries/BlogPostsQuery"
import {useEventPostsQuery} from "~/queries/EventPostsQuery"
import {useLabelsQuery} from "~/queries/LabelsQuery"
import {useLinksQuery} from "~/queries/LinksQuery"
import {PostItem, PostTypes} from "~/types/PostItem"
import {generateQueryParams} from "~/utilities/queryParams"
import ContactUsForm from "../components/Forms/ContactUsForm"
import {SEO} from "../components/UI/SEO/SEO"
import SocialShare from "../components/UI/SocialShare/SocialShare"
import {PageContext} from "../types/PageContext"
import "./MagazinePost.css"
import {MagazineSearchParam, MagazineSectionParam, SectionIndex, sectionsIndexToname} from "./MagazineTemplate"

type MagazinePostContext = PageContext<
    Queries.MagazinePostQuery["allContentfulContenutoMagazine"]["edges"][0]["node"],
    Queries.MagazinePostQuery["allContentfulPaginaMagazine"]["edges"][0]["node"]
>

interface MagazinePostProps extends PageProps<any, MagazinePostContext> {}

const MagazinePost: React.FC<MagazinePostProps> = props => {
    const magazinePostData = props.pageContext.data
    const magazinePageData = props.pageContext.secondaryData

    const i18n = useContext(I18nContext)
    const links = useLinksQuery()
    const labels = useLabelsQuery()
    const magazinePosts: Array<PostItem> = []
    const allMagazinePosts = [...useBlogPostsQuery(), ...useAcademyPostsQuery(), ...useEventPostsQuery()]
    allMagazinePosts.forEach(post => {
        if (post.type !== magazinePostData.type) return
        if (magazinePostData.slug === post.slug) return
        magazinePosts.push(post as PostItem)
    })

    let shelfLabel: string | undefined | null = ""
    switch (magazinePostData.type) {
        case PostTypes.blog:
            if (!labels.moreArticles) break
            shelfLabel = labels.moreArticles
            break
        case PostTypes.events:
            if (!labels.moreEvents) break
            shelfLabel = labels.moreEvents
            break
        case PostTypes.academy:
            if (!labels.more) break
            shelfLabel = labels.more
            break
    }

    const [selectedSectionIndex, setSelectedSectionIndex] = useState<SectionIndex | number>(0)
    const [inputTextValue, setInputTextValue] = useState("")
    const [selectedSectionName, setSelectedSectionName] = useState<ShelfSortCategories | string>("")
    const transitionContenxt = useContext(TransitionContext)

    const onInputChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputTextValue(event.currentTarget.value)
    }
    const onSwitchClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
        const selectedType = Number(event.currentTarget.value)
        setSelectedSectionIndex(selectedType)
        setSelectedSectionName(sectionsIndexToname(selectedType))
    }
    const onFormSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const queryParams = generateQueryParams(
            {name: MagazineSectionParam, value: selectedSectionName},
            {name: MagazineSearchParam, value: inputTextValue},
        )
        const url = `${links.magazine.url}?${queryParams}`
        transitionContenxt.navigateTo(url)
    }

    let formattedDate
    if (magazinePostData.date) {
        formattedDate = new Intl.DateTimeFormat(i18n.language, {
            day: "numeric",
            month: "long",
            year: "numeric",
        }).format(new Date(magazinePostData.date))
    }

    return (
        <Fragment>
            <HeroSection image={magazinePostData.image?.gatsbyImageData} imageAlt={magazinePostData.image?.title}>
                <div>
                    <h6 className="date-9jgh">{formattedDate}</h6>
                    <h3 className="title-9jgh">{magazinePostData.title}</h3>
                    <h6 className="category-9jgh">
                        {`${labels.category}: `}
                        <GenericLink
                            className="category-value-9jgh"
                            to={`${links.magazine.url}?search=${magazinePostData.category}&scroll=true`}
                        >
                            {magazinePostData.category}
                        </GenericLink>
                    </h6>
                </div>
            </HeroSection>
            <SectionContainer>
                <Search title={magazinePageData?.titolo} startClosed>
                    <Gap centered style={{marginTop: "var(--gutter-4)"}}>
                        <Switch
                            key={labels.all}
                            selected={selectedSectionIndex === SectionIndex.index}
                            onSelect={onSwitchClickHandler}
                            value={SectionIndex.index}
                        >
                            {labels.all}
                        </Switch>
                        {magazinePageData?.sezioni?.map((section, index) => {
                            return (
                                <Switch
                                    key={section?.slug}
                                    selected={selectedSectionIndex === index + 1}
                                    onSelect={onSwitchClickHandler}
                                    value={index + 1}
                                >
                                    {section?.titolo}
                                </Switch>
                            )
                        })}
                    </Gap>
                    <form onSubmit={onFormSubmitHandler}>
                        <Gap centered>
                            <InputText
                                placeholder={labels.searchInputPlaceholder}
                                onChange={onInputChangeValue}
                                value={inputTextValue}
                                style={{
                                    width: "min(30vw,500px)",
                                    minWidth: "240px",
                                    fontStyle: "italic",
                                }}
                            />
                            <Button color="green" type="submit">
                                {labels.search}
                            </Button>
                        </Gap>
                    </form>
                </Search>
            </SectionContainer>
            <SectionContainer>
                <Blank>
                    <SocialShare layout="magazine" />
                    {magazinePostData.content && <Reader>{magazinePostData.content}</Reader>}
                </Blank>
            </SectionContainer>
            <SectionContainer className="contact-us-section-9jgh">
                <ContactUsForm />
            </SectionContainer>
            <SectionContainer>
                <Shelf show={2} label={shelfLabel}>
                    {magazinePosts.map((item, index) => {
                        return (
                            <Post
                                key={index}
                                date={item.date}
                                title={item.title}
                                image={item.image}
                                category={item.category}
                                tags={item.tags}
                                description_short={item.description_short}
                                slug={item.slug}
                                type={item.type}
                                customer={item.customer}
                                project={item.project}
                            />
                        )
                    })}
                </Shelf>
            </SectionContainer>
        </Fragment>
    )
}

export default MagazinePost

export const Head: HeadFC<any, MagazinePostContext> = props => {
    const magazinePostData = props.pageContext.data

    const links = useLinksQuery()

    let canonicalUrl = ""
    switch (magazinePostData.type) {
        case PostTypes.blog:
            canonicalUrl = `${process.env.SITE_URL}${links.blog.url}/${magazinePostData.slug}`
            break
        case PostTypes.events:
            canonicalUrl = `${process.env.SITE_URL}${links.events.url}/${magazinePostData.slug}`
            break
        case PostTypes.academy:
            canonicalUrl = `${process.env.SITE_URL}${links.academy.url}/${magazinePostData.slug}`
            break
    }

    return (
        <SEO
            title={magazinePostData.title}
            description={magazinePostData.description_short}
            language={props.pageContext.language}
            {...(magazinePostData.immagineSocialShare
                ? {
                      image: magazinePostData.immagineSocialShare?.url,
                      imageAlt: magazinePostData.immagineSocialShare?.title,
                  }
                : {image: magazinePostData.image?.url, imageAlt: magazinePostData.image?.title})}
            siteUrl={canonicalUrl}
        />
    )
}
